import React, { useEffect, useRef } from 'react';
import { RemoteParticipant, Track } from 'livekit-client';

interface ParticipantTileProps {
  participant: RemoteParticipant;
  isPinned: boolean;
  isHost: boolean;
  onPin: () => void;
}

// Renders a single participant's video + audio and attaches it to DOM elements.
export const ParticipantTile: React.FC<ParticipantTileProps> = ({
  participant,
  isPinned,
  isHost,
  onPin,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const attachTracks = () => {
      participant.getTrackPublications().forEach((pub) => {
        if (!pub.track || !pub.isSubscribed) return;
        if (pub.track.kind === Track.Kind.Video && videoRef.current) {
          pub.track.attach(videoRef.current);
        }
        if (pub.track.kind === Track.Kind.Audio && audioRef.current) {
          pub.track.attach(audioRef.current);
        }
      });
    };

    attachTracks();
    participant.on('trackSubscribed', attachTracks);
    participant.on('trackUnsubscribed', attachTracks);

    return () => {
      participant.off('trackSubscribed', attachTracks);
      participant.off('trackUnsubscribed', attachTracks);
    };
  }, [participant]);

  return (
    <div style={{
      position: 'relative',
      background: 'var(--bg-raised)',
      border: isPinned ? '2px solid var(--accent)' : '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      aspectRatio: '16/9',
      cursor: isHost ? 'pointer' : 'default',
    }}
    onClick={isHost ? onPin : undefined}
    title={isHost ? 'Click to spotlight' : undefined}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />

      {/* Name badge */}
      <div style={{
        position: 'absolute', bottom: 8, left: 8,
        background: 'rgba(0,0,0,0.6)', borderRadius: 'var(--radius-sm)',
        padding: '3px 8px', fontSize: 12, color: '#fff', fontWeight: 500,
        backdropFilter: 'blur(4px)',
      }}>
        {participant.name ?? participant.identity}
      </div>

      {/* Pinned badge */}
      {isPinned && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: 'var(--accent)', color: '#1a0050',
          borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 11, fontWeight: 600,
        }}>
          LIVE
        </div>
      )}

      {/* Host hint overlay */}
      {isHost && !isPinned && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0)', transition: 'background 0.18s',
          fontSize: 12, color: 'rgba(255,255,255,0)', pointerEvents: 'none',
        }}
        className="pin-hint"
        >
          Spotlight
        </div>
      )}
    </div>
  );
};
